import type { ListingViewModel } from '../types/listings/ListingViewModel';
import type { SoldListingSummaryViewModel } from '../types/conversation/ListingSummaryViewModel';
import { MediaService } from '../services/MediaService';

/**
 * Normalize API specs into Record<string, string>.
 * Handles: null/undefined, array of { key/name, value }, or object with non-string values.
 */
export function normalizeSpecs(specs: unknown): Record<string, string> {
  if (specs == null) return {};
  if (Array.isArray(specs)) {
    const out: Record<string, string> = {};
    for (const item of specs) {
      if (item && typeof item === 'object') {
        const key = (item as { key?: string; name?: string }).key ?? (item as { key?: string; name?: string }).name;
        const val = (item as { value?: unknown }).value;
        if (key != null && key !== '' && val != null) {
          out[String(key)] = String(val);
        }
      }
    }
    return out;
  }
  if (typeof specs === 'object') {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(specs)) {
      if (k != null && k !== '' && v != null && v !== '') {
        out[k] = String(v);
      }
    }
    return out;
  }
  return {};
}

/**
 * Canonical spec key for icon/label lookup (Turkish and English API keys).
 * Keys here are lowercase; canonicalSpecKey lowercases input before lookup.
 */
const SPEC_KEY_ALIASES: Record<string, string> = {
  brand: 'brand',
  marka: 'brand',
  model: 'model',
  screenSize: 'screenSize',
  'ekran boyutu': 'screenSize',
  resolution: 'resolution',
  'ekran çözünürlüğü': 'resolution',
  processor: 'processor',
  işlemci: 'processor',
  cpu: 'processor',
  gpu: 'gpu',
  'ekran kartı': 'gpu',
  ram: 'ram',
  ssd: 'ssd',
  hdd: 'hdd',
};

function canonicalSpecKey(key: string): string {
  const lower = key.trim().toLowerCase();
  return SPEC_KEY_ALIASES[lower] ?? lower;
}

/** Icon class suffix for Font Awesome (e.g. 'microchip' -> 'fa-microchip'). */
export function getSpecIcon(key: string): string {
  const icons: Record<string, string> = {
    brand: 'laptop',
    model: 'info-circle',
    screenSize: 'desktop',
    resolution: 'expand',
    processor: 'microchip',
    gpu: 'tv',
    ram: 'memory',
    ssd: 'solid fa-hard-drive',
    hdd: 'hdd',
  };
  return icons[canonicalSpecKey(key)] ?? 'info-circle';
}

/** Turkish label for spec key. */
export function getSpecLabel(key: string): string {
  const labels: Record<string, string> = {
    brand: 'Marka',
    model: 'Model',
    screenSize: 'Ekran Boyutu',
    resolution: 'Ekran Çözünürlüğü',
    processor: 'İşlemci',
    gpu: 'Ekran Kartı',
    ram: 'RAM',
    ssd: 'SSD',
    hdd: 'HDD',
    condition: 'Durum',
    slottype: 'Slot Tipi',
    'slot tipi': 'Slot Tipi',
    frequency: 'Frekans',
    frekans: 'Frekans',
    mhz: 'Frekans (MHz)',
    capacity: 'Kapasite',
    kapasite: 'Kapasite',
  };
  const canonical = canonicalSpecKey(key);
  return labels[canonical] ?? key;
}

/** Preferred display order for known spec keys; unknown keys follow alphabetically. */
const SPEC_DISPLAY_ORDER = [
  'brand',
  'model',
  'processor',
  'gpu',
  'ram',
  'ssd',
  'hdd',
  'screenSize',
  'resolution',
  'capacity',
  'slottype',
  'frequency',
  'condition',
];

function specSortIndex(key: string): number {
  const canonical = canonicalSpecKey(key);
  const idx = SPEC_DISPLAY_ORDER.indexOf(canonical);
  return idx >= 0 ? idx : SPEC_DISPLAY_ORDER.length;
}

/** Sort spec entries for consistent listing detail layout. */
export function sortSpecEntries(specs: Record<string, string>): [string, string][] {
  return Object.entries(specs).sort(([keyA], [keyB]) => {
    const orderDiff = specSortIndex(keyA) - specSortIndex(keyB);
    if (orderDiff !== 0) return orderDiff;
    return getSpecLabel(keyA).localeCompare(getSpecLabel(keyB), 'tr-TR');
  });
}

/** Human-readable value with light unit normalization for common numeric specs. */
export function formatSpecValue(key: string, value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  const canonical = canonicalSpecKey(key);
  const lowerKey = key.trim().toLowerCase();
  const hasUnit = /\b(gb|tb|mb|mhz|ghz|mm|inç|inch)\b/i.test(trimmed);

  if (!hasUnit) {
    if (canonical === 'ram' || canonical === 'ssd' || canonical === 'hdd' || lowerKey.includes('kapasite')) {
      if (/^\d+$/.test(trimmed)) return `${trimmed} GB`;
    }
    if (lowerKey.includes('mhz') || lowerKey.includes('frekans')) {
      if (/^\d+$/.test(trimmed)) return `${trimmed} MHz`;
    }
    if (lowerKey.includes('ghz') && /^\d+([.,]\d+)?$/.test(trimmed)) {
      return `${trimmed} GHz`;
    }
  }

  return trimmed;
}

/**
 * Product shape for listing detail pages (Ilan_detay, Ilan_detay_login, Ilan_detay_kurumsal).
 */
export interface ListingDetailProduct {
  id: string;
  /** Human-friendly listing code (ilan no) shown in UI. */
  listingCode?: string;
  title: string;
  price: number;
  description: string;
  images: string[];
  seller: {
    name: string;
    joinDate: string;
    avatar: string;
    /**
     * Backend-computed canonical display name (FR-020). Feature 010 replaces the
     * "Üye: <joinDate>" placeholder with this value on the listing-detail page.
     */
    displayName?: string;
    /** `"Personal"` or `"Corporate"` — drives the profile-link route selection. */
    accountType?: 'Personal' | 'Corporate';
  };
  specs: Record<string, string>;
  performance: {
    games: Record<string, { fps: number; minFps: number; maxFps: number }>;
    apps: Record<string, { compatible: boolean; requirements: string }>;
  };
  location: {
    address: string;
    district: string;
    city: string;
    coordinates: { lat: number; lng: number };
  };
  productTypeName?: string;
  conditionName?: string;
  /** Top-level category name (e.g. "Bilgisayar") for feature-gating UI sections. */
  topCategoryName?: string | null;
  /** Admin-verified tested listing badge on detail card. */
  isTested?: boolean;
  viewCount?: number;
  sellerId?: string;
  statusId?: number;
  statusName?: string;
  /** When Rejected and API allows — moderator message for owner/admin. */
  rejectionReason?: string;
  /** When true, seller opted to show profile phone on detail. */
  showPhoneOnListing?: boolean;
  /** Seller phone from profile; only when authenticated and showPhoneOnListing. */
  sellerPhoneNumber?: string | null;
  /**
   * Two-phase sale projection passed through from backend. Drives buyer-verify
   * and rate-seller CTAs. Null when the listing is not part of an active sale.
   */
  soldListingSummary?: SoldListingSummaryViewModel | null;
}

const PLACEHOLDER_IMAGE =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext fill="%23999" x="50%25" y="50%25" dominant-baseline="middle" text-anchor="middle"%3EGörsel yok%3C/text%3E%3C/svg%3E';

/** Default avatar for seller when no profile image: user silhouette. */
const PLACEHOLDER_AVATAR =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23adb5bd"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';

/**
 * Map ListingViewModel to ListingDetailProduct for detail pages.
 */
export function listingViewModelToDetailProduct(lv: ListingViewModel): ListingDetailProduct {
  const rawUrls =
    lv.imageUrls ??
    lv.images?.map((i) => i.url).filter((u): u is string => Boolean(u)) ??
    [];
  const urls = rawUrls
    .map((url) => MediaService.toImageSrc(url))
    .filter((url): url is string => Boolean(url));
  const images = urls.length > 0 ? urls : [PLACEHOLDER_IMAGE];
  const district = lv.district?.name ?? '';
  const city = lv.city?.name ?? '';
  const sellerJoinDate = lv.seller?.joinDate
    ? new Date(lv.seller.joinDate).toLocaleDateString('tr-TR')
    : '-';
  return {
    id: lv.id,
    listingCode: lv.listingCode ?? undefined,
    title: lv.title ?? '',
    price: lv.price,
    description: lv.description ?? '',
    images,
    seller: {
      name: lv.seller?.displayName ?? 'Satıcı',
      joinDate: sellerJoinDate,
      avatar: PLACEHOLDER_AVATAR,
      displayName: lv.seller?.displayName ?? undefined,
      accountType: lv.seller?.accountType ?? undefined,
    },
    specs: normalizeSpecs(lv.specs),
    performance: { games: {}, apps: {} },
    location: {
      address: '',
      district,
      city,
      coordinates: { lat: 0, lng: 0 },
    },
    productTypeName: lv.productType?.name ?? undefined,
    conditionName: lv.condition?.name ?? undefined,
    topCategoryName: lv.productType?.secondaryCategory?.topCategory?.name ?? null,
    isTested: lv.isTested === true,
    viewCount: lv.viewCount,
    sellerId: lv.userId,
    statusId: lv.statusId,
    statusName: lv.status?.name ?? undefined,
    rejectionReason: lv.rejectionReason ?? undefined,
    soldListingSummary: lv.soldListingSummary ?? null,
    showPhoneOnListing: lv.showPhoneOnListing === true,
    sellerPhoneNumber: lv.sellerPhoneNumber ?? null,
  };
}
